import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { CustomFormData } from 'shared/types/account/Account';
import { EDIT_ADDRESS_OPTIONS } from '../../../../../constants/constant';
import { CustomFloatingSelect } from '../../customer-detail/components/customSelect';
import { Button } from '../../ui/button';
import { Label } from '../../ui/customLabel/customLabel';
import { RadioGroup, RadioGroupItem } from '../../ui/customRadio/customRadio';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../ui/dialog';
import Loader from '../../ui/loader';
import { maskPhoneNumber, maskPostalCode } from '../../utils/formatUtils';
import AddAddressForm from '../common/addressCommonForm';
import EditAddressModal from '../customerDetailAction/editAddress';

interface EditAddress {
  Texts: { [key: string]: string };
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  addresses: CustomFormData[];
  handleSubmitEditAddress: (value?: string) => void;
  handleChangeAddress: () => void;
  loading: boolean;
  shippingAddress: CustomFormData;
  getUpdatedAddress: (addressId: string, data: CustomFormData) => void;
  onOrderShippingAddressUpdated?: (address: CustomFormData) => void;
  customerId: string;
  handleAddressChange: (id: string) => void;
  selectedAddress: CustomFormData;
  setSelectedAddress: Dispatch<SetStateAction<CustomFormData>>;
  handleSubmitAddress: () => void;
}
const EditAddress = ({
  Texts,
  open,
  setOpen,
  addresses,
  handleSubmitEditAddress,
  handleChangeAddress,
  loading,
  shippingAddress,
  getUpdatedAddress,
  onOrderShippingAddressUpdated,
  customerId,
  handleAddressChange,
  selectedAddress,
  setSelectedAddress,
  handleSubmitAddress,
}: EditAddress) => {
  const [selectedOption, setSelectedOption] = useState(EDIT_ADDRESS_OPTIONS.SAVED_ADDRESS.value);
  const [openEditModal, setOpenEditModal] = useState(false);
  const isEditingOrderShippingAddress = selectedAddress?.addressId === shippingAddress?.addressId;

  useEffect(() => {
    if (addresses?.length === 0) {
      handleChangeAddress();
    }
  }, []);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-h-[90%] max-w-[600px] overflow-y-auto bg-white sm:rounded-md">
        {loading && <Loader />}
        <DialogHeader>
          <DialogTitle className="mb-4 flex flex-col">
            <p className="text-2xl font-bold">{Texts?.shippingAddress}</p>{' '}
            <p className="text-sm font-normal">{Texts?.requiredFields}</p>
          </DialogTitle>
          <DialogDescription>
            <RadioGroup value={selectedOption} onValueChange={setSelectedOption} className="mb-6">
              <section className="flex items-center gap-6">
                <span className="mb-4 mr-4 flex items-center">
                  <RadioGroupItem
                    value={EDIT_ADDRESS_OPTIONS.NEW_ADDRESS.value}
                    id={EDIT_ADDRESS_OPTIONS.NEW_ADDRESS.id}
                    className="mr-2 border-black"
                  />
                  <Label htmlFor={EDIT_ADDRESS_OPTIONS.NEW_ADDRESS.id}>{Texts.newAddress}</Label>
                </span>
                <span className="mb-4 mr-4 flex items-center">
                  <RadioGroupItem
                    value={EDIT_ADDRESS_OPTIONS.SAVED_ADDRESS.value}
                    id={EDIT_ADDRESS_OPTIONS.SAVED_ADDRESS.id}
                    className="mr-2 border-black"
                  />
                  <Label htmlFor={EDIT_ADDRESS_OPTIONS.SAVED_ADDRESS.id}>{Texts.savedAddress}</Label>
                </span>
              </section>
            </RadioGroup>
            {selectedOption === EDIT_ADDRESS_OPTIONS.SAVED_ADDRESS.value ? (
              <section>
                <CustomFloatingSelect
                  label={'Select Address'}
                  onChange={(addressId) => handleAddressChange(addressId)}
                  id={'shippingAddress'}
                  placeholder={'*Select Address'}
                  defaultValue={selectedAddress?.addressId}
                  className="mb-4 w-[400px]"
                  options={addresses?.map((items: any) => (
                    <option key={items?.addressId} value={items?.addressId} className="px-2">
                      {items?.fullName}
                    </option>
                  ))}
                />
                {selectedAddress && (
                  <ul>
                    <li className="flex text-lg">
                      <p className="w-2/3">
                        {selectedAddress?.firstName} {selectedAddress?.lastName}
                        <br />
                        {selectedAddress?.streetName}
                        <br />
                        {selectedAddress?.city} {maskPostalCode(selectedAddress?.postalCode ?? '')}
                        <br />
                        {maskPhoneNumber(selectedAddress?.phone ?? '')}
                      </p>
                    </li>
                  </ul>
                )}
                <button className="text-blueLight underline" onClick={() => setOpenEditModal(true)}>
                  {Texts.editAddress}
                </button>
                {openEditModal && (
                  <EditAddressModal
                    Texts={Texts}
                    data={selectedAddress}
                    open={openEditModal}
                    setOpen={setOpenEditModal}
                    updateAddress={(addressId: string, data: any) => setSelectedAddress({ ...data, addressId })}
                    customerId={customerId}
                    makeAPIcall={isEditingOrderShippingAddress}
                    getUpdatedAddress={!isEditingOrderShippingAddress ? getUpdatedAddress : undefined}
                    onSuccessfulUpdate={
                      isEditingOrderShippingAddress
                        ? (addressId: string, data: CustomFormData) => {
                            onOrderShippingAddressUpdated?.({ ...data, addressId });
                          }
                        : undefined
                    }
                  ></EditAddressModal>
                )}
              </section>
            ) : (
              <AddAddressForm
                Texts={Texts}
                customerId={customerId}
                AddNewAddress={(data: any) => console.error(data)}
                getAddresses={handleSubmitAddress}
                onClose={() => setOpen(false)}
              />
            )}
          </DialogDescription>
        </DialogHeader>

        {selectedOption === EDIT_ADDRESS_OPTIONS.SAVED_ADDRESS.value ? (
          <section className="mt-4 flex justify-start gap-4">
            <Button
              className="rounded-md bg-deepBlue p-4 text-white"
              onClick={() => handleSubmitEditAddress(selectedAddress?.addressId)}
            >
              {Texts.saveButton}
            </Button>
            <Button className="p-0 underline" onClick={() => setOpen(false)}>
              {Texts.cancel}
            </Button>
          </section>
        ) : (
          <></>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EditAddress;
